import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, API_URL } from '../../services/apiService';
import { Article, CATEGORY_LABELS } from '../../types';
import { resolveImageUrl } from '../../utils/images';
import jsPDF from 'jspdf';

const ContentDesk: React.FC = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(''); // YYYY-MM-DD; '' = all dates
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  // Inline confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    message: string; onConfirm: () => void;
  } | null>(null);
  // Inline error/success toast
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, kind: 'success' | 'error') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isToday = (dateStr?: string) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    return d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  };

  // True when the article's generation date (created_at) falls on the given
  // selected calendar day. `selected` is a YYYY-MM-DD string from <input type="date">.
  // Uses LOCAL time to match isToday() and the displayed formatDate().
  const isOnDate = (dateStr?: string, selected?: string) => {
    if (!dateStr || !selected) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    // Build the local Y-M-D of the article date and compare to the selected string.
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}` === selected;
  };

  useEffect(() => {
    apiService.getArticles().then(data => {
      if (data?.articles) setArticles(data.articles);
    }).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const validKeys = ['bauxite', 'alumina', 'primary-aluminium', 'downstream-products', 'end-user', 'recycled-aluminium', 'sustainability', 'supplement-aluminium'];
    const used = new Set(articles.map(a => a.category).filter(Boolean));
    return validKeys.filter(k => used.has(k));
  }, [articles]);

  const filtered = useMemo(() => {
    const result = articles.filter(a => {
      if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
      if (dateFilter && !isOnDate(a.created_at, dateFilter)) return false;
      if (statusFilter === 'today') return isToday(a.created_at);
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return a.headline.toLowerCase().includes(q) || a.content.toLowerCase().includes(q) || (a.news_source || '').toLowerCase().includes(q);
      }
      return true;
    });
    return result.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db - da;
    });
  }, [articles, categoryFilter, statusFilter, searchQuery, dateFilter]);

  const todayCount = useMemo(() => articles.filter(a => isToday(a.created_at)).length, [articles]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: articles.length, today: todayCount };
    articles.forEach(a => { counts[a.status] = (counts[a.status] || 0) + 1; });
    return counts;
  }, [articles, todayCount]);

  const TAB_STATUSES = [
    { key: 'all',          label: 'All' },
    { key: 'today',        label: 'Today' },
    { key: 'draft',        label: 'Ready for Review' },
    { key: 'needs_review', label: 'Needs Review' },
    { key: 'published',    label: 'Published' },
    { key: 'revised',      label: 'Revised' },
  ];

  const handleClearAll = () => {
    setConfirmDialog({
      message: `Delete all ${articles.length} articles? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          for (const article of articles) await apiService.deleteArticle(article.id);
          setArticles([]);
        } catch (err) {
          showToast('Failed to clear — ' + (err as Error).message, 'error');
          const data = await apiService.getArticles();
          if (data?.articles) setArticles(data.articles);
        }
      },
    });
  };

  const handleDeleteArticle = (article: Article, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDialog({
      message: `Delete "${article.headline.slice(0, 60)}…"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await apiService.deleteArticle(article.id);
          setArticles(prev => prev.filter(a => a.id !== article.id));
        } catch (err) {
          showToast('Failed to delete — ' + (err as Error).message, 'error');
        }
      },
    });
  };

  const handleDownloadPDF = async (article: Article, e: React.MouseEvent) => {
    e.stopPropagation();
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 50;
    const contentW = pageW - margin * 2;
    let y = 40;

    const checkY = (needed: number) => {
      if (y + needed > pageH - margin) { doc.addPage(); y = margin; }
    };

    doc.setFillColor(232, 136, 42);
    doc.rect(0, 0, pageW, 6, 'F');

    // Date
    const articleDate = article.created_at ? new Date(article.created_at) : new Date();
    const dateStr = articleDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(115, 115, 115);
    doc.text(dateStr, pageW - margin, y, { align: 'right' });
    y += 18;

    // Category
    if (article.category) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(232, 136, 42);
      const catLabel = CATEGORY_LABELS[article.category] || article.category;
      doc.text(catLabel.toUpperCase(), margin, y);
      y += 14;
    }

    // Headline
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(15, 19, 28);
    const lines = doc.splitTextToSize(article.headline, contentW);
    doc.text(lines, margin, y);
    y += lines.length * 20 + 6;

    // Sources list
    const pdfSources: { name: string; url: string }[] =
      article.all_sources && article.all_sources.length > 0
        ? article.all_sources
        : article.news_source
          ? [{ name: article.news_source, url: article.news_url || '' }]
          : [];
    if (pdfSources.length > 0) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(115, 115, 115);
      doc.text(`SOURCES (${pdfSources.length})`, margin, y);
      y += 12;
      pdfSources.forEach((s, i) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(115, 115, 115);
        const num = `${i + 1}. `;
        doc.text(num, margin, y);
        const numW = doc.getTextWidth(num);
        doc.setTextColor(30, 64, 175);
        if (s.url) {
          doc.textWithLink(s.name || s.url, margin + numW, y, { url: s.url });
        } else {
          doc.text(s.name, margin + numW, y);
        }
        y += 12;
      });
      y += 4;
    }

    // Divider
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    // Image
    const rawImageUrl = article.generated_image_url || article.imageUrl;
    if (rawImageUrl) {
      const imageUrl = rawImageUrl.startsWith('/')
        ? `${API_URL}/api/image-proxy?path=${encodeURIComponent(rawImageUrl)}`
        : rawImageUrl;
      try {
        const imgDataUrl: string = await new Promise(async (resolve, reject) => {
          const resp = await fetch(imageUrl);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(blob);
        });
        const imgEl = await new Promise<HTMLImageElement>((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(img);
          img.src = imgDataUrl;
        });
        const imgMaxW = contentW;
        const imgMaxH = 240;
        const aspectRatio = imgEl.naturalWidth > 0 ? imgEl.naturalHeight / imgEl.naturalWidth : 9 / 16;
        const imgH = Math.min(imgMaxH, imgMaxW * aspectRatio);
        checkY(imgH + 16);
        const fmt = imgDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
        doc.addImage(imgDataUrl, fmt, margin, y, imgMaxW, imgH);
        y += imgH + 16;
      } catch {
        // Image unavailable — continue without it
      }
    }

    // Body
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    for (const para of article.content.split(/\n\n+/)) {
      const t = para.trim();
      if (!t) continue;
      const pl = doc.splitTextToSize(t, contentW);
      checkY(pl.length * 14 + 8);
      doc.text(pl, margin, y);
      y += pl.length * 14 + 8;
    }

    // Footer
    const totalPages = (doc.internal as any).getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(220, 220, 220);
      doc.line(margin, pageH - 36, pageW - margin, pageH - 36);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 22, { align: 'right' });
    }

    doc.save(`${article.headline.replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.pdf`);
  };


  return (
    <div className="flex flex-col w-full gap-4">

      {/* ── Confirm dialog ─────────────────────────────────────────── */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}>
          <div className="rounded-2xl p-6 max-w-sm w-full shadow-2xl" style={{ background: '#ffffff' }}>
            <div className="flex items-start gap-3 mb-5">
              <span className="material-symbols-outlined text-[22px] flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }}>warning</span>
              <p className="font-body text-body-md" style={{ color: '#0f172a', lineHeight: 1.6 }}>{confirmDialog.message}</p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 rounded-xl font-body text-body-sm transition-all"
                style={{ background: '#f1f5f9', color: '#475569' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="px-4 py-2 rounded-xl font-body text-body-sm transition-all"
                style={{ background: '#ef4444', color: '#ffffff' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────── */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg font-body text-body-sm"
          style={{
            background: toast.kind === 'error' ? '#fef2f2' : 'rgba(52,211,153,0.12)',
            border: `1px solid ${toast.kind === 'error' ? '#fecaca' : 'rgba(52,211,153,0.4)'}`,
            color: toast.kind === 'error' ? '#dc2626' : '#065f46',
          }}
        >
          <span className="material-symbols-outlined text-[18px]">{toast.kind === 'error' ? 'error' : 'check_circle'}</span>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-1 opacity-50 hover:opacity-100">
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-headline-lg text-on-surface tracking-tight">Articles</h1>
          <p className="font-body text-body-sm text-on-surface-variant">{articles.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          {articles.length > 0 && (
            <button
              onClick={handleClearAll}
              className="px-3 py-1.5 bg-surface-container-high hover:bg-error-container text-on-surface-variant hover:text-error font-body text-body-sm rounded-lg transition-all flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[15px]">delete_sweep</span>
              Clear All
            </button>
          )}
          <button
            onClick={() => navigate('/pipeline?trigger=true')}
            className="px-3 py-1.5 bg-primary-container hover:bg-primary text-on-primary font-body text-body-sm rounded-lg transition-all flex items-center gap-1.5"
          >
            <span className="material-symbols-outlined text-[15px]">add</span>
            New Articles
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex items-center bg-white border border-gray-200 rounded-lg px-3 gap-2 flex-1 min-w-[200px]">
          <span className="material-symbols-outlined text-outline text-[16px]">search</span>
          <input
            className="w-full bg-transparent py-2 text-on-surface placeholder:text-outline/60 text-body-sm font-body focus:outline-none"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-outline hover:text-on-surface">
              <span className="material-symbols-outlined text-[15px]">close</span>
            </button>
          )}
        </div>

        {/* Category filter */}
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="bg-white border border-gray-200 text-on-surface font-body text-body-sm rounded-lg px-3 py-2 focus:outline-none cursor-pointer"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat} ({articles.filter(a => a.category === cat).length})</option>
          ))}
        </select>

        {/* Date filter — show only articles generated on the selected day */}
        <div className="relative flex items-center bg-white border border-gray-200 rounded-lg px-3 gap-2">
          <span className="material-symbols-outlined text-outline text-[16px]">calendar_today</span>
          <input
            type="date"
            value={dateFilter}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setDateFilter(e.target.value)}
            title="Filter by generation date"
            className="bg-transparent py-2 text-on-surface text-body-sm font-body focus:outline-none cursor-pointer"
          />
          {dateFilter && (
            <button
              onClick={() => setDateFilter('')}
              title="Clear date filter"
              className="text-outline hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-[15px]">close</span>
            </button>
          )}
        </div>

        {/* View toggle */}
        <div className="flex items-center bg-surface-container rounded-lg p-0.5 gap-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-surface-container-high text-primary' : 'text-outline hover:text-on-surface'}`}
          >
            <span className="material-symbols-outlined text-[16px]">grid_view</span>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-surface-container-high text-primary' : 'text-outline hover:text-on-surface'}`}
          >
            <span className="material-symbols-outlined text-[16px]">view_list</span>
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex items-center gap-1 border-b border-surface-container-high pb-0 overflow-x-auto">
        {TAB_STATUSES.map(tab => {
          const count = tab.key === 'all' ? articles.length : (statusCounts[tab.key] || 0);
          if (tab.key !== 'all' && tab.key !== 'today' && count === 0) return null;
          const active = statusFilter === tab.key;
          const isAlert = tab.key === 'needs_review';
          const isToday_ = tab.key === 'today';
          return (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-3 py-2 text-body-sm font-body whitespace-nowrap border-b-2 transition-all flex items-center gap-1.5 -mb-px ${
                active
                  ? isAlert
                    ? 'border-error text-error font-semibold'
                    : 'border-transparent font-semibold'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface'
              }`}
              style={
                active && !isAlert
                  ? { borderBottomColor: '#4f46e5', color: '#4f46e5' }
                  : undefined
              }
            >
              {isAlert && <span className="material-symbols-outlined text-[13px]">warning</span>}
              {isToday_ && <span className="material-symbols-outlined text-[13px]">today</span>}
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                active
                  ? isAlert ? 'bg-error/15 text-error' : isToday_ ? 'bg-emerald-400/15 text-emerald-400' : 'bg-primary/15 text-primary'
                  : 'bg-surface-container-high text-outline'
              }`}>{count}</span>
            </button>
          );
        })}
        <span className="ml-auto text-outline font-body text-[11px] pr-1 whitespace-nowrap hidden sm:block">
          {filtered.length} of {articles.length}
        </span>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(article => {
            const imageUrl = resolveImageUrl(article.generated_image_url || article.imageUrl);
            const hasImage = !!imageUrl;
            const isNeedsReview = article.status === 'needs_review';
            const isHovered = hoveredCardId === article.id;

            return (
              <div
                key={article.id}
                className={`group rounded-xl overflow-hidden transition-all duration-200 cursor-pointer flex flex-col ${
                  isNeedsReview ? 'ring-1 ring-error/40' : ''
                }`}
                style={{
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.07)',
                  boxShadow: isHovered
                    ? '0 8px 24px rgba(0,0,0,0.1)'
                    : '0 1px 4px rgba(0,0,0,0.06)',
                  transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                }}
                onClick={() => navigate(`/article/${article.id}`)}
                onMouseEnter={() => setHoveredCardId(article.id)}
                onMouseLeave={() => setHoveredCardId(null)}
              >
                {/* Thumbnail */}
                {hasImage ? (
                  <div className="relative h-48 w-full overflow-hidden bg-surface-container-high flex-shrink-0">
                    <img
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                      <CategoryBadge category={article.category || ''} />
                      <StatusBadge status={article.status} />
                    </div>
                  </div>
                ) : (
                  <div className="h-1.5 w-full bg-gradient-to-r from-primary/40 to-primary/10" />
                )}

                {/* Body */}
                <div className="p-3 flex flex-col gap-2 flex-1">
                  {/* Top row when no image */}
                  {!hasImage && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <CategoryBadge category={article.category || ''} />
                      <StatusBadge status={article.status} />
                      <span className="ml-auto flex items-center gap-1.5">
                        {isToday(article.created_at) && (
                          <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary font-mono text-[10px] font-semibold">NEW</span>
                        )}
                        <span className="text-on-surface-variant font-mono text-[11px] flex items-center gap-1">
                          <span className="material-symbols-outlined text-[13px]">calendar_today</span>
                          {formatDate(article.created_at)}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Source */}
                  <span className="text-outline font-body text-[11px] truncate">{article.news_source}</span>

                  {/* Headline */}
                  <h2 className="font-display text-headline-sm text-on-surface leading-snug group-hover:text-primary transition-colors line-clamp-2">
                    {article.headline}
                  </h2>

                  {/* Tags row */}
                  {article.tags && article.tags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {article.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 rounded bg-surface-container-lowest text-outline font-body text-[10px]">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Needs review banner */}
                  {isNeedsReview && (
                    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-error/8 rounded-lg border border-error/20">
                      <span className="material-symbols-outlined text-error text-[14px]">flag</span>
                      <span className="font-body text-[11px] text-error font-medium">Flagged for human review</span>
                    </div>
                  )}

                  {/* Scores & Date */}
                  <div className="flex items-center gap-3 mt-auto pt-1 border-t border-surface-container-high/40">
                    <ScorePill label="Style" value={article.style_match_score || 0} />
                    <ScorePill label="Original" value={article.originality_score || (100 - (article.plagiarism_score || 0))} />
                    <ScorePill label="Quality" value={article.style_match_score || article.confidence_score || 0} />
                    <span className="ml-auto flex items-center gap-1.5">
                      {isToday(article.created_at) && (
                        <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary font-mono text-[10px] font-semibold">NEW</span>
                      )}
                      <span className="flex items-center gap-1 text-on-surface-variant font-mono text-[11px]">
                        <span className="material-symbols-outlined text-[13px]">calendar_today</span>
                        {formatDate(article.created_at)}
                      </span>
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5">
                    <button
                      className={`flex-1 py-1.5 px-3 font-body text-body-sm rounded-lg transition-all flex items-center justify-center gap-1 ${
                        article.status === 'published'
                          ? 'bg-surface-container-high hover:bg-surface-bright text-on-surface'
                          : ''
                      }`}
                      style={
                        article.status !== 'published'
                          ? {
                              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                              color: '#ffffff',
                            }
                          : undefined
                      }
                      onClick={e => { e.stopPropagation(); navigate(`/article/${article.id}`); }}
                    >
                      <span className="material-symbols-outlined text-[14px]">
                        {article.status === 'published' ? 'visibility' : 'edit_note'}
                      </span>
                      {article.status === 'published' ? 'View' : 'Review & Publish'}
                    </button>
                    <button
                      className="p-1.5 rounded-lg transition-all"
                      style={{ background: '#f1f5f9', color: '#64748b' }}
                      onClick={e => { e.stopPropagation(); navigate(`/feedback?articleId=${article.id}`); }}
                      title="Give feedback"
                    >
                      <span className="material-symbols-outlined text-[15px]">rate_review</span>
                    </button>
                    <button
                      className="p-1.5 bg-surface-container-high hover:bg-surface-bright text-on-surface-variant hover:text-on-surface rounded-lg transition-all"
                      onClick={e => handleDownloadPDF(article, e)}
                      title="Download PDF"
                    >
                      <span className="material-symbols-outlined text-[15px]">download</span>
                    </button>
                    <button
                      className="p-1.5 bg-surface-container-high hover:bg-error-container text-on-surface-variant hover:text-error rounded-lg transition-all"
                      onClick={e => handleDeleteArticle(article, e)}
                      title="Delete"
                    >
                      <span className="material-symbols-outlined text-[15px]">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-[40px] text-outline/30 mb-3">article</span>
              <h3 className="font-display text-headline-sm text-on-surface mb-1">No articles found</h3>
              <p className="font-body text-body-sm text-on-surface-variant">
                {dateFilter
                  ? `No articles generated on ${formatDate(dateFilter)}`
                  : searchQuery ? `No results for "${searchQuery}"` : 'Generate new articles to get started.'}
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Table View */
        <div
          className="glass-card overflow-hidden"
          style={{ background: '#ffffff', border: '1px solid rgba(0,0,0,0.07)' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-surface-container-lowest">
                <tr className="text-outline font-body text-[11px] uppercase tracking-wider">
                  <th className="py-2.5 px-4 font-semibold">Article</th>
                  <th className="py-2.5 px-3 font-semibold">Category</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Style</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Original</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Date</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container-high/30">
                {filtered.map(article => {
                  const isNeedsReview = article.status === 'needs_review';
                  return (
                    <tr
                      key={article.id}
                      className={`hover:bg-surface-container/50 transition-colors cursor-pointer ${isNeedsReview ? 'bg-error/5' : ''}`}
                      onClick={() => navigate(`/article/${article.id}`)}
                    >
                      <td className="py-2.5 px-4 max-w-xs">
                        <div className="flex items-center gap-2.5">
                          {(article.generated_image_url || article.imageUrl) && (
                            <img
                              src={resolveImageUrl(article.generated_image_url || article.imageUrl)}
                              alt=""
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-surface-container-high"
                            />
                          )}
                          <div className="min-w-0">
                            <h3 className="font-body text-body-sm text-on-surface line-clamp-1">{article.headline}</h3>
                            <span className="font-body text-[11px] text-outline">{article.news_source}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <CategoryBadge category={article.category || ''} />
                      </td>
                      <td className="py-2.5 px-3">
                        <StatusBadge status={article.status} />
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-mono text-body-sm font-bold ${(article.style_match_score || 0) >= 80 ? 'text-emerald-400' : (article.style_match_score || 0) >= 60 ? 'text-primary' : 'text-error'}`}>
                          {article.style_match_score || 0}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-mono text-body-sm font-bold ${(article.plagiarism_score || 0) <= 30 ? 'text-emerald-400' : 'text-error'}`}>
                          {article.originality_score || (100 - (article.plagiarism_score || 0))}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className="flex items-center justify-end gap-1.5">
                          {isToday(article.created_at) && (
                            <span className="px-1.5 py-0.5 rounded bg-primary/15 text-primary font-mono text-[10px] font-semibold">NEW</span>
                          )}
                          <span className="text-on-surface-variant font-mono text-[11px]">{formatDate(article.created_at)}</span>
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            className="p-1.5 text-outline hover:text-primary rounded-lg hover:bg-surface-container-high transition-all"
                            onClick={e => { e.stopPropagation(); navigate(`/article/${article.id}`); }}
                            title="Open"
                          >
                            <span className="material-symbols-outlined text-[16px]">open_in_new</span>
                          </button>
                          <button
                            className="p-1.5 text-outline hover:text-primary rounded-lg hover:bg-surface-container-high transition-all"
                            onClick={e => handleDownloadPDF(article, e)}
                            title="Download PDF"
                          >
                            <span className="material-symbols-outlined text-[16px]">download</span>
                          </button>
                          <button
                            className="p-1.5 text-outline hover:text-error rounded-lg hover:bg-error-container/20 transition-all"
                            onClick={e => handleDeleteArticle(article, e)}
                            title="Delete"
                          >
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-10 text-center text-outline font-body text-body-sm">
                No articles match your filters
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { text: string; bg: string; dot: string; label: string }> = {
    draft:        { text: 'text-primary',      bg: 'bg-primary/10',      dot: 'bg-primary',      label: 'Ready for Review' },
    published:    { text: 'text-emerald-400',  bg: 'bg-emerald-400/10',  dot: 'bg-emerald-400',  label: 'Published' },
    revised:      { text: 'text-amber-400',    bg: 'bg-amber-400/10',    dot: 'bg-amber-400',    label: 'Revised' },
    review:       { text: 'text-secondary',    bg: 'bg-secondary/10',    dot: 'bg-secondary',    label: 'In Review' },
    needs_review: { text: 'text-error',        bg: 'bg-error/10',        dot: 'bg-error',        label: 'Needs Review' },
  };
  const c = config[status] || { text: 'text-outline', bg: 'bg-surface-container', dot: 'bg-outline', label: status };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-body text-[10px] font-medium ${c.text} ${c.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`} />
      {c.label}
    </span>
  );
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'bauxite':              { bg: 'bg-amber-400/10',    text: 'text-amber-400',    border: 'border-amber-400/30' },
  'alumina':              { bg: 'bg-emerald-400/10',  text: 'text-emerald-400',  border: 'border-emerald-400/30' },
  'primary-aluminium':    { bg: 'bg-primary/10',      text: 'text-primary',      border: 'border-primary/30' },
  'downstream-products':  { bg: 'bg-purple-400/10',   text: 'text-purple-400',   border: 'border-purple-400/30' },
  'end-user':             { bg: 'bg-[#ec4899]/10',    text: 'text-[#ec4899]',    border: 'border-[#ec4899]/30' },
  'recycled-aluminium':   { bg: 'bg-emerald-500/10',  text: 'text-emerald-500',  border: 'border-emerald-500/30' },
  'sustainability':       { bg: 'bg-[#16a34a]/10',    text: 'text-[#16a34a]',    border: 'border-[#16a34a]/30' },
  'supplement-aluminium': { bg: 'bg-[#ea580c]/10',    text: 'text-[#ea580c]',    border: 'border-[#ea580c]/30' },
};


const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const c = CATEGORY_COLORS[category] || { bg: 'bg-surface-container-high', text: 'text-on-surface-variant', border: 'border-surface-container-highest' };
  const label = CATEGORY_LABELS[category] || category || 'Uncategorized';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border font-body text-[11px] font-semibold uppercase tracking-wider ${c.bg} ${c.text} ${c.border}`}>
      {label}
    </span>
  );
};

const ScorePill: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const color = value >= 85 ? 'text-emerald-400' : value >= 70 ? 'text-primary' : value >= 50 ? 'text-amber-400' : 'text-error';
  return (
    <div className="flex flex-col items-center text-center">
      <span className="text-outline font-body text-[10px]">{label}</span>
      <span className={`font-mono text-[12px] font-bold ${color}`}>{Math.round(value)}%</span>
    </div>
  );
};

export default ContentDesk;
